import { z } from 'zod';
import { AccountStatus, CompanyRole, Cursor, Id, Limit, Palette, Timestamp, singleLine } from './common';
import { BINARY, endpoint } from './endpoint';
import { lenient } from './lenient';

/** The stored photo's version: the start of its SHA-256, so a new photo always gets a new URL. */
const AvatarVersion = z.string().regex(/^[0-9a-f]{16,64}$/);

/**
 * A profile photo, served by `users.avatar` under this version. Absent means
 * no photo: none uploaded, removed, or the account is deactivated.
 */
export const AvatarRef = z.object({ version: z.string().min(1).max(64) });
export type AvatarRef = z.infer<typeof AvatarRef>;

/** A colleague as shown in lists, avatars and the people picker. */
export const Person = z.object({
  id: Id,
  displayName: z.string(),
  /** Two-letter avatar label (see `shortNameOf`). */
  shortName: z.string(),
  palette: lenient(Palette, 'p1'),
  department: z.string(),
  email: z.string(),
  status: lenient(AccountStatus, 'deactivated'),
  online: z.boolean(),
  /** Optional like every field added to a response; one this app cannot read shows the initials. */
  avatar: lenient(AvatarRef.optional(), undefined),
  /**
   * Rises with every change to the person as others see them (`user.updated` carries it too), so an
   * older copy never replaces a newer one. Absent from older servers: every copy counts as current.
   */
  revision: lenient(z.number().int().nonnegative().optional(), undefined),
});
export type Person = z.infer<typeof Person>;

export const Me = Person.extend({
  role: lenient(CompanyRole, 'member'),
  mfaEnabled: z.boolean(),
  createdAt: Timestamp,
  /**
   * This account's invitations go out without an admin's approval: always for admins, for members when the
   * owner allows direct invites. The server decides each invitation anyway; absent on older servers.
   */
  directInvite: z.boolean().optional(),
});
export type Me = z.infer<typeof Me>;

export const usersEndpoints = {
  me: endpoint({ id: 'users.me', method: 'GET', path: '/api/v1/me', auth: 'session', response: Me }),
  /** Name and department; a change is announced to the whole company (`user.updated`), hence its own rate limit. */
  updateMe: endpoint({
    id: 'users.updateMe',
    method: 'PATCH',
    path: '/api/v1/me',
    auth: 'session',
    body: z.object({
      displayName: singleLine({ min: 1, max: 40 }).optional(),
      department: singleLine({ max: 60 }).optional(),
    }),
    response: Me,
    rateLimit: 'profile',
  }),
  /**
   * Sets the signed-in member's photo from a still JPEG, PNG or WebP of at most
   * `LIMITS.maxAvatarBytes` (raw bytes). The server decodes it and stores its own
   * square WebP without metadata; anything else is FILE_TYPE_NOT_ALLOWED.
   */
  setAvatar: endpoint({
    id: 'users.setAvatar',
    method: 'PUT',
    path: '/api/v1/me/avatar',
    auth: 'session',
    body: z.custom<Blob | ArrayBuffer | Uint8Array>(),
    rawBody: true,
    response: Me,
    rateLimit: 'avatar',
  }),
  removeAvatar: endpoint({ id: 'users.removeAvatar', method: 'DELETE', path: '/api/v1/me/avatar', auth: 'session', response: Me }),
  /** The stored photo (image/webp), cacheable for good: another version is another photo. Other versions are NOT_FOUND. */
  avatar: endpoint({
    id: 'users.avatar',
    method: 'GET',
    path: '/api/v1/users/:userId/avatar/:version',
    auth: 'session',
    params: z.object({ userId: Id, version: AvatarVersion }),
    response: BINARY,
  }),
  /** Company directory (active and deactivated accounts, deactivated flagged). */
  list: endpoint({
    id: 'users.list',
    method: 'GET',
    path: '/api/v1/users',
    auth: 'session',
    query: z.object({
      q: z.string().trim().max(100).optional(),
      includeDeactivated: z.stringbool().optional(),
      cursor: Cursor.optional(),
      limit: Limit(200, 100),
    }),
    response: z.object({ users: z.array(Person), nextCursor: Cursor.nullable() }),
    rateLimit: 'search',
  }),
};
